import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, X, Loader2 } from '../../icons';
import { getReservationStatus, type ReservationDto, type ReservationResourceDto } from '../../api/reservation';
import { getDateLocale } from '../../lib/base-i18n';
import { tCalendar } from '../../lib/calendar-i18n';
import { isReservationInThePast } from '../../lib/reservation-time';
import { describeCalendarError, type FriendlyError } from './calendarErrors';
import { CalendarErrorNotice } from './CalendarErrorNotice';
import { formatAmPmTime as hm } from './time-label';
import { maxConcurrent } from './room-availability';
import './RoomStatusModal.css';
import './EventCreateSide.css';

/**
 * 회의실 예약 현황 — 방 × 시간 표 (2026-10-16 · 사용자가 고른 시안 C).
 *
 * ⭐ 2026-10-19 — 일정 추가 창 «오른쪽에 나란히» 뜨는 옆 창이다(사용자 결정 · 시안 확정안). 회의실 카드를 켜거나
 *   고른 방의 「수정」을 누르면 열린다. 배경·자기 포커스 가두기는 없다 — 두 창을 함께 감싼 일정 추가 창이 맡는다.
 * 하루치 모든 방의 예약을 한 표에 그리고,
 *   - 파란 점선 = 지금 일정의 시간
 *   - 빈 칸을 누르면 그 방과 «그 30분»이 골라진다. 끌거나 Shift 를 누른 채 누르면 여러 칸을 잇는다
 *     → 「회의실 예약하기」로 일정 창의 방·시작·종료에 반영
 *     ⭐ 2026-10-16 사용자 지적 — 종전에는 «누른 칸부터 일정 길이만큼»이라, 17:00 칸을 눌러도 원래 선택(17~18시)과
 *       같아 «안 눌린» 듯했고, 17:30 칸은 17:30~18:30(운영 시간 밖)이 잡혀 30분만 보였다.
 *   - 하루종일 일정은 시간을 바꾸지 않고 방만 고른다
 * 자리가 없거나, 이미 지났거나, 그 방의 운영 시간 밖인 칸은 누를 수 없다. 최종 판정은 저장할 때 서버가 한다.
 *
 * Esc 는 여기서 먼저 받아 `preventDefault` 한다(일정 창은 그 Esc 를 넘긴다) — 이 창만 닫힌다.
 * 남의 예약의 사유는 서버가 지워 보낸다(/reservation/status).
 */
export interface RoomStatusModalProps {
  /** 표에 그릴 방(고르개가 거른 목록 그대로). */
  rooms: ReservationResourceDto[];
  /** 현황을 부를 분류(하나일 때만). */
  category?: string;
  /** 지금 일정의 시간 — 점선과 처음 고른 칸. */
  eventRange: { start: Date; end: Date };
  /** 지금 골라 둔 방('' = 없음). */
  initialRoomId: string;
  /** 이 일정이 이미 잡아 둔 예약(수정 화면) — 표에 «남의 예약»으로 그리지도, 자리 셈에 넣지도 않는다. */
  ignoreReservationId?: string | null;
  /** 하루종일 — 칸을 눌러도 시간은 그대로, 방만 고른다. */
  allDay?: boolean;
  onChoose: (room: ReservationResourceDto, start: Date, end: Date) => void;
  onClose: () => void;
}

const MIN = 60 * 1000;
const DAY_MS = 24 * 60 * MIN;
const SLOT_MIN = 30;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);

export function RoomStatusModal({
  rooms, category, eventRange, initialRoomId, ignoreReservationId, allDay = false, onChoose, onClose,
}: RoomStatusModalProps) {
  const [day, setDay] = useState(() => startOfDay(eventRange.start));
  const [rows, setRows] = useState<ReservationDto[] | null>(null);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [sel, setSel] = useState(() => ({ roomId: initialRoomId, start: eventRange.start, end: eventRange.end }));
  /** 끌어 고르는 중의 시작 칸(같은 방 줄 안에서만 잇는다). Shift+누르기의 기준점이기도 하다. */
  const anchorRef = useRef<{ roomId: string; idx: number } | null>(null);
  const draggingRef = useRef(false);
  useEffect(() => {
    const up = () => { draggingRef.current = false; };
    window.addEventListener('pointerup', up);
    return () => window.removeEventListener('pointerup', up);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    getReservationStatus(day, new Date(day.getTime() + DAY_MS), category)
      .then((st) => { if (!cancelled) setRows(st.reservations.filter((r) => r.id !== ignoreReservationId)); })
      .catch((err) => { if (!cancelled) setError(describeCalendarError(err, 'room_schedule')); });
    return () => { cancelled = true; };
  }, [day, category, ignoreReservationId]);

  /* 시간 축 — 방들의 운영 시간을 덮고, 그날의 이 일정도 덮는다(기본 9~18시). */
  const dayMs = day.getTime();
  const [h0, h1] = useMemo(() => {
    let a = Math.min(...rooms.map((r) => Math.floor(r.openMinute / 60)).filter(Number.isFinite), 24);
    let b = Math.max(...rooms.map((r) => Math.ceil(r.closeMinute / 60)).filter(Number.isFinite), 0);
    if (!(a < b)) { a = 9; b = 18; }
    const es = eventRange.start.getTime();
    const ee = eventRange.end.getTime();
    if (es < dayMs + DAY_MS && ee > dayMs && !allDay) {
      a = Math.min(a, Math.max(0, Math.floor((es - dayMs) / (60 * MIN))));
      b = Math.max(b, Math.min(24, Math.ceil((ee - dayMs) / (60 * MIN))));
    }
    return [a, b];
  }, [rooms, eventRange, dayMs, allDay]);
  const axisStart = dayMs + h0 * 60 * MIN;
  const axisEnd = dayMs + h1 * 60 * MIN;
  const span = axisEnd - axisStart;
  /** [s, e) 를 축 위의 % 로 — 축 밖이면 null. */
  const place = (s: number, e: number) => {
    const a = Math.max(s, axisStart);
    const b = Math.min(e, axisEnd);
    if (!(a < b)) return null;
    return { left: `${((a - axisStart) / span) * 100}%`, width: `${((b - a) / span) * 100}%` };
  };
  const hours = Array.from({ length: h1 - h0 }, (_, i) => h0 + i);
  const slotCount = ((h1 - h0) * 60) / SLOT_MIN;

  const now = new Date();
  /** 이 방의 [s, e) 가 막혔나 — 지난 시간 · 운영 시간 밖 · 자리 없음(그날 현황이 있을 때만 센다 — 다른 날은 서버가 판정). */
  const blocked = (room: ReservationResourceDto, s: Date, e: Date) => {
    if (isReservationInThePast(e, now)) return true;
    if (!allDay) {
      const d0 = startOfDay(s).getTime();
      if (s.getTime() < d0 + room.openMinute * MIN || e.getTime() > d0 + room.closeMinute * MIN) return true;
    }
    if (!rows || startOfDay(s).getTime() !== dayMs) return false;
    return maxConcurrent(rows.filter((r) => r.resourceId === room.id), s, e) >= Math.max(1, room.capacity);
  };
  /** 칸 i 의 30분. */
  const cell = (i: number) => {
    const s = new Date(axisStart + i * SLOT_MIN * MIN);
    return { start: s, end: new Date(s.getTime() + SLOT_MIN * MIN) };
  };
  /** 칸을 누를 수 있나 — 하루종일이면 시간을 안 바꾸므로 지금 고른 시간으로 본다. */
  const cellOff = (room: ReservationResourceDto, i: number) => {
    if (allDay) return blocked(room, sel.start, sel.end);
    const c = cell(i);
    return blocked(room, c.start, c.end);
  };
  /**
   * 칸 i 를 고른다. `extend` 면 같은 방의 기준 칸부터 i 까지 잇는다 — 사이에 막힌 칸이 있으면 그 앞까지만.
   * 하루종일이면 시간은 그대로 두고 방만 바꾼다.
   */
  const pickCell = (room: ReservationResourceDto, i: number, extend: boolean) => {
    if (cellOff(room, i)) return;
    if (allDay) { setSel((s) => ({ ...s, roomId: room.id })); return; }
    const a = anchorRef.current;
    if (extend && a && a.roomId === room.id) {
      const step = i >= a.idx ? 1 : -1;
      let last = a.idx;
      for (let k = a.idx + step; step > 0 ? k <= i : k >= i; k += step) {
        if (cellOff(room, k)) break;
        last = k;
      }
      const lo = Math.min(a.idx, last);
      const hi = Math.max(a.idx, last);
      setSel({ roomId: room.id, start: cell(lo).start, end: cell(hi).end });
      return;
    }
    anchorRef.current = { roomId: room.id, idx: i };
    setSel({ roomId: room.id, ...cell(i) });
  };

  const selRoom = rooms.find((r) => r.id === sel.roomId) ?? null;
  const selBlocked = selRoom ? blocked(selRoom, sel.start, sel.end) : true;
  const band = startOfDay(eventRange.start).getTime() === dayMs || startOfDay(eventRange.end).getTime() === dayMs
    ? place(eventRange.start.getTime(), eventRange.end.getTime())
    : null;
  /* 좁은 화면에서는 표가 옆으로 넘친다 — 열 때·날을 바꿀 때 이 일정 시간(점선)이 보이게 옮겨 둔다. */
  const scrollRef = useRef<HTMLDivElement>(null);
  const hasBand = band !== null;
  useLayoutEffect(() => {
    const box = scrollRef.current;
    const el = box?.querySelector<HTMLElement>('.roomstat-band');
    if (!box || !el || box.scrollWidth <= box.clientWidth) return;
    const b = el.getBoundingClientRect();
    const c = box.getBoundingClientRect();
    box.scrollLeft += b.left + b.width / 2 - (c.left + c.width / 2);
  }, [dayMs, hasBand]);

  const dayLabel = day.toLocaleDateString(getDateLocale(), { month: 'long', day: 'numeric', weekday: 'short' });
  const selDay = sel.start.toLocaleDateString(getDateLocale(), { month: 'long', day: 'numeric', weekday: 'short' });
  const selDayDiffers = startOfDay(sel.start).getTime() !== startOfDay(eventRange.start).getTime();

  return (
      <div
        className="roomstat"
        role="dialog"
        aria-labelledby="roomstat-title"
        onKeyDown={(e) => {
          // 일정 창까지 닫히지 않게 — useEscapeClose 는 defaultPrevented 인 Esc 를 넘긴다.
          if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); }
        }}
      >
        <header className="roomstat-head">
          <h3 id="roomstat-title">{tCalendar('calendar.room.status_title')}</h3>
          <div className="roomstat-nav">
            <button type="button" className="roomstat-navbtn" onClick={() => setDay((d) => addDays(d, -1))} aria-label={tCalendar('calendar.room.prev_day')}>
              <ChevronLeft size={16} />
            </button>
            <span className="roomstat-day">{dayLabel}</span>
            <button type="button" className="roomstat-navbtn" onClick={() => setDay((d) => addDays(d, 1))} aria-label={tCalendar('calendar.room.next_day')}>
              <ChevronRight size={16} />
            </button>
          </div>
          <ul className="roomstat-legend" aria-label={tCalendar('calendar.room.legend')}>
            <li><span className="roomstat-swatch roomstat-swatch--approved" />{tCalendar('calendar.room.status_approved')}</li>
            <li><span className="roomstat-swatch roomstat-swatch--pending" />{tCalendar('calendar.room.status_pending')}</li>
            <li><span className="roomstat-swatch roomstat-swatch--mine" />{tCalendar('calendar.room.mine')}</li>
            <li><span className="roomstat-swatch roomstat-swatch--event" />{tCalendar('calendar.room.this_event')}</li>
          </ul>
          <button type="button" className="roomstat-x" onClick={onClose} aria-label={tCalendar('calendar.btn.close')}>
            <X size={18} />
          </button>
        </header>

        <div className="roomstat-body">
          {error ? (
            <CalendarErrorNotice className="roomstat-msg" error={error} />
          ) : (
            <div className="roomstat-scroll" ref={scrollRef}>
              <div className="roomstat-grid" style={{ '--roomstat-slots': slotCount } as React.CSSProperties}>
                <span />
                <div className="roomstat-hours" aria-hidden>
                  {hours.map((h) => (
                    <span key={h} style={{ left: `${((h - h0) / (h1 - h0)) * 100}%` }}>{h}</span>
                  ))}
                </div>

                <div className="roomstat-names" role="radiogroup" aria-label={tCalendar('calendar.room.status_title')}>
                  {rooms.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      role="radio"
                      aria-checked={sel.roomId === r.id}
                      className={`roomstat-name${sel.roomId === r.id ? ' is-sel' : ''}`}
                      disabled={blocked(r, sel.start, sel.end)}
                      onClick={() => setSel((s) => ({ ...s, roomId: r.id }))}
                    >
                      <b>{r.name}</b>
                      <span>{[r.location, r.requiresApproval ? tCalendar('calendar.room.needs_approval') : null].filter(Boolean).join(' · ')}</span>
                    </button>
                  ))}
                </div>

                <div className="roomstat-tracks">
                  {rooms.map((r) => (
                    <div key={r.id} className={`roomstat-row${sel.roomId === r.id ? ' is-sel' : ''}`}>
                      <div className="roomstat-track">
                        {Array.from({ length: slotCount }, (_, i) => (
                          <button
                            key={i}
                            type="button"
                            className="roomstat-slot"
                            disabled={cellOff(r, i)}
                            aria-label={`${r.name} ${hm(cell(i).start)}`}
                            // 마우스·터치: 누르는 순간 고르고, 누른 채 옆 칸으로 가면 잇는다.
                            onPointerDown={(e) => {
                              if (e.button !== 0) return;
                              pickCell(r, i, e.shiftKey);
                              draggingRef.current = true;
                              // 터치는 누른 요소가 포인터를 붙잡는다 — 놓아 줘야 옆 칸이 pointerenter 를 받는다.
                              (e.target as Element).releasePointerCapture?.(e.pointerId);
                            }}
                            onPointerEnter={() => { if (draggingRef.current) pickCell(r, i, true); }}
                            // 키보드(Enter·Space)는 pointerdown 이 없다 — click 의 detail 이 0 이다.
                            onClick={(e) => { if (e.detail === 0) pickCell(r, i, e.shiftKey); }}
                          />
                        ))}
                        {(rows ?? []).filter((x) => x.resourceId === r.id).map((x) => {
                          const pos = place(new Date(x.startAt).getTime(), new Date(x.endAt).getTime());
                          if (!pos) return null;
                          const kind = x.mine ? 'mine' : x.status === 'pending' ? 'pending' : 'approved';
                          return (
                            <span key={x.id} className={`roomstat-block roomstat-block--${kind}`} style={pos}>
                              {tCalendar(kind === 'mine' ? 'calendar.room.mine' : kind === 'pending' ? 'calendar.room.status_pending' : 'calendar.room.status_approved')}
                            </span>
                          );
                        })}
                        {sel.roomId === r.id && (() => {
                          const pos = place(sel.start.getTime(), sel.end.getTime());
                          return pos ? (
                            <span className={`roomstat-block roomstat-block--sel${selBlocked ? ' is-bad' : ''}`} style={pos}>
                              {tCalendar('calendar.room.selected')}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  ))}
                  {band && <span className="roomstat-band" style={band} aria-hidden />}
                </div>
              </div>
              {rows === null && (
                <p className="roomstat-msg"><Loader2 size={13} className="calendar-spin" aria-hidden /> {tCalendar('calendar.room.loading')}</p>
              )}
            </div>
          )}
          <p className="roomstat-hint">
            {tCalendar(allDay ? 'calendar.room.status_hint_allday' : 'calendar.room.status_hint', { range: `${hm(eventRange.start)} – ${hm(eventRange.end)}` })}
          </p>
        </div>

        <footer className="roomstat-foot">
          <span className="roomstat-picked">
            {selRoom ? (
              <>
                {tCalendar('calendar.room.picked')} <b>{selRoom.name}</b>
                {' · '}{selDayDiffers ? `${selDay} ` : ''}{hm(sel.start)} – {hm(sel.end)}
              </>
            ) : tCalendar('calendar.room.pick_prompt')}
          </span>
          <button
            type="button"
            className="roomstat-ok"
            disabled={!selRoom || selBlocked}
            onClick={() => { if (selRoom) onChoose(selRoom, sel.start, sel.end); }}
          >
            {tCalendar('calendar.room.choose')}
          </button>
        </footer>
      </div>
  );
}
